import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

const ClinicContext = createContext(null);

export const useClinic = () => {
  const context = useContext(ClinicContext);
  if (!context) {
    throw new Error('useClinic must be used within ClinicProvider');
  }
  return context;
};

export const ClinicProvider = ({ children }) => {
  const { api, token, user } = useAuth();
  
  const [patients, setPatients] = useState([]);
  const [queue, setQueue] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [patientQuery, setPatientQuery] = useState('');
  const [patientPage, setPatientPage] = useState(0);
  const [patientTotal, setPatientTotal] = useState(0);
  const [patientShowAll, setPatientShowAll] = useState(false);
  const [error, setError] = useState('');
  const patientPageSize = 50;
  const directoryRequest = useRef(0);
  const queueRequest = useRef(0);
  const patientRequest = useRef(0);

  const clearPatientDirectory = useCallback(() => {
    directoryRequest.current += 1;
    setPatients([]);
    setPatientTotal(0);
    setLoading(false);
    setError('');
  }, []);

  const loadPatientDirectory = useCallback(async ({ query = '', page = 0, showAll = false } = {}) => {
    if (!token) return;
    const term = String(query || '').trim();
    if (!term && !showAll) {
      clearPatientDirectory();
      return;
    }

    const request = ++directoryRequest.current;
    setLoading(true);
    try {
      const response = await api().get('/v2/patients/directory', {
        params: {
          q: term,
          skip: Math.max(0, Number(page) || 0) * patientPageSize,
          limit: patientPageSize,
          show_all: Boolean(showAll && !term),
        },
      });
      if (request !== directoryRequest.current) return;
      setPatients(response.data?.patients || []);
      setPatientTotal(response.data?.total || 0);
      setError('');
    } catch (loadError) {
      if (request !== directoryRequest.current) return;
      console.error('Failed to load patient directory:', loadError);
      setPatients([]);
      setPatientTotal(0);
      setError('Patient directory could not be refreshed. Retry before opening a record.');
    } finally {
      if (request === directoryRequest.current) setLoading(false);
    }
  }, [api, token, clearPatientDirectory]);

  const loadQueue = useCallback(async () => {
    if (!token) return;
    const request = ++queueRequest.current;
    try {
      const response = await api().get('/queue');
      if (request === queueRequest.current) setQueue(response.data || []);
    } catch (queueError) {
      if (request === queueRequest.current) console.error('Failed to load waiting room:', queueError);
    }
  }, [api, token]);

  const loadDashboardData = useCallback(async () => {
    await Promise.all([
      loadPatientDirectory({ query: patientQuery, page: patientPage, showAll: patientShowAll }),
      loadQueue(),
    ]);
  }, [loadPatientDirectory, loadQueue, patientQuery, patientPage, patientShowAll]);

  const loadPatient = useCallback(async (patientId) => {
    const request = ++patientRequest.current;
    try {
      const response = await api().get(`/patients/${patientId}`);
      if (request === patientRequest.current) setSelectedPatient(response.data);
      return response.data;
    } catch (loadError) {
      console.error('Failed to load patient:', loadError);
      return null;
    }
  }, [api]);

  const updatePatient = useCallback(async (patientId, data) => {
    try {
      await api().put(`/patients/${patientId}`, data);
      await loadDashboardData();
      if (selectedPatient?.patient_id === patientId) {
        await loadPatient(patientId);
      }
      return { success: true };
    } catch (updateError) {
      return { success: false, error: updateError.response?.data?.detail || 'Update failed' };
    }
  }, [api, loadDashboardData, loadPatient, selectedPatient]);

  const deletePatient = useCallback(async (patientId, password) => {
    try {
      await api().delete(`/patients/${patientId}`, { params: { password } });
      setSelectedPatient(null);
      await loadDashboardData();
      return { success: true };
    } catch (deleteError) {
      return { success: false, error: deleteError.response?.data?.detail || 'Delete failed' };
    }
  }, [api, loadDashboardData]);

  const getPatientVisits = useCallback(async (patientId) => {
    try {
      const response = await api().get(`/visits/${patientId}`);
      return response.data;
    } catch (loadError) {
      console.error('Failed to load visits:', loadError);
      return [];
    }
  }, [api]);

  const createVisit = useCallback(async (data) => {
    try {
      const response = await api().post('/visits', data);
      await loadDashboardData();
      return { success: true, visit_id: response.data.visit_id };
    } catch (createError) {
      return { success: false, error: createError.response?.data?.detail || 'Failed to create visit' };
    }
  }, [api, loadDashboardData]);

  const getPatientAudit = useCallback(async (patientId) => {
    try {
      const response = await api().get(`/patients/${patientId}/audit`);
      return response.data;
    } catch (loadError) {
      console.error('Failed to load audit:', loadError);
      return [];
    }
  }, [api]);

  const accessKey = JSON.stringify([user?.username, user?.role, user?.restricted_access, user?.department_ids, user?.clinic_wide_visibility]);
  useEffect(() => {
    directoryRequest.current += 1;
    queueRequest.current += 1;
    patientRequest.current += 1;
    setPatients([]);
    setPatientTotal(0);
    setSelectedPatient(null);
    setQueue([]);
    sessionStorage.removeItem('jvc_recent_views_v1');
  }, [accessKey]);

  // Waiting-room activity stays live. The full patient directory is loaded only after a search or Show all.
  useEffect(() => {
    if (token) {
      loadQueue();
      const interval = setInterval(loadQueue, 30000);
      return () => { clearInterval(interval); queueRequest.current += 1; };
    }
  }, [token, loadQueue]);

  return (
    <ClinicContext.Provider value={{
      patients,
      patientQuery, setPatientQuery, patientPage, setPatientPage, patientTotal, patientPageSize,
      patientShowAll, setPatientShowAll, error,
      queue,
      selectedPatient,
      setSelectedPatient,
      loading,
      loadDashboardData,
      loadPatientDirectory,
      clearPatientDirectory,
      loadQueue,
      loadPatient,
      updatePatient,
      deletePatient,
      getPatientVisits,
      createVisit,
      getPatientAudit
    }}>
      {children}
    </ClinicContext.Provider>
  );
};
